import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, TextField, MenuItem, Typography, InputAdornment, IconButton,
  Alert, CircularProgress, Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  LockOutlined as LockOutlinedIcon,
  LockReset as LockResetIcon,
  Visibility, VisibilityOff,
  CheckCircle as CheckIcon,
  Cancel as CrossIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import { BRAND, brandFieldSx } from '../../../brand';
import { GENDERS } from '../options';
import StepHeading from '../StepHeading';
import { FormSection, FormRow, Field } from '../FormLayout';
import { setup_username_available } from '../../BaseURL';

// ============================================================================
//  Step 3 - Super Administrator
// ============================================================================
//  Creates the one account that can reach everything. Two behaviours here are
//  deliberate and are mirrored by the server (setup_routes.js), not just
//  advisory in the UI:
//
//    1. The username is PERMANENT. It is chosen once, checked for
//       availability live, and can never be edited afterwards - only the
//       password can change. The field says so plainly, because there is no
//       "undo" once setup commits.
//    2. The password set here is a HANDOVER password. On first sign-in the
//       system forces the administrator to replace it, so the person who
//       installed the system does not retain a working credential.

function AdminStep({ data, errors, onChange, onUsernameStatus }) {
  const set = (field) => (e) => onChange(field, e.target.value);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  // null = untested, 'checking' | 'available' | 'taken'
  const [nameState, setNameState] = useState({ status: null, message: '' });

  const adorn = (Icon) => ({
    startAdornment: (
      <InputAdornment position="start">
        <Icon sx={{ fontSize: 19, color: BRAND.navy }} />
      </InputAdornment>
    ),
  });

  // -- Live username availability -------------------------------------------
  // Debounced so a fast typist does not fire a request per keystroke. The
  // effect owns a `cancelled` flag so a stale response cannot overwrite the
  // verdict for a newer query.
  useEffect(() => {
    const username = (data.username || '').trim();

    if (!username) {
      setNameState({ status: null, message: '' });
      onUsernameStatus?.(null);
      return undefined;
    }

    setNameState({ status: 'checking', message: 'Checking availability...' });
    onUsernameStatus?.('checking');

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data: res } = await axios.get(setup_username_available, { params: { username } });
        if (cancelled) return;
        const status = res.available ? 'available' : 'taken';
        setNameState({ status, message: res.message || '' });
        onUsernameStatus?.(status);
      } catch (err) {
        if (cancelled) return;
        // A failed check must not read as "available" - the server re-checks
        // on submit anyway, so degrade to neutral rather than to a green tick.
        setNameState({ status: null, message: 'Could not verify right now - it will be re-checked when you finish.' });
        onUsernameStatus?.(null);
      }
    }, 450);

    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.username]);

  const nameAdornment = () => {
    if (nameState.status === 'checking')  return <CircularProgress size={16} sx={{ color: BRAND.inkFaint }} />;
    if (nameState.status === 'available') return <CheckIcon sx={{ fontSize: 20, color: BRAND.green }} />;
    if (nameState.status === 'taken')     return <CrossIcon sx={{ fontSize: 20, color: BRAND.danger }} />;
    return null;
  };

  const usernameHelper =
    errors.username
      ? errors.username
      : nameState.message || 'Permanent. 4-32 characters - letters, numbers, dot, underscore or hyphen.';

  return (
    <Box>
      <StepHeading
        icon={AdminIcon}
        title="Super Administrator"
        subtitle="The first and most privileged account on this system. It can create every other user, configure every module, and is the only account that cannot be locked out by another administrator."
      />

      <Alert
        severity="info" icon={<InfoIcon />}
        sx={{
          mb: 1, borderRadius: '12px',
          background: BRAND.navySoft, color: BRAND.ink,
          border: `1px solid ${BRAND.navy}22`,
          '& .MuiAlert-icon': { color: BRAND.navy },
        }}
      >
        Set a temporary password here &mdash; any password is accepted, and the
        administrator must replace it at their first sign-in. <strong>The username,
        however, is permanent and cannot be changed afterwards.</strong>
      </Alert>

      {/* -- Personal details -------------------------------------------- */}
      <FormSection
        title="Personal details"
        caption="Identifies the person behind the account in audit logs and on printed documents."
      >
        <FormRow>
          <Field span={6}>
            <TextField
              fullWidth required
              label="Full Name"
              placeholder="e.g. Dr. Ama Mensah"
              value={data.fullName}
              onChange={set('fullName')}
              error={!!errors.fullName}
              helperText={errors.fullName || ' '}
              sx={brandFieldSx}
              InputProps={adorn(PersonIcon)}
            />
          </Field>

          <Field span={3}>
            <TextField
              fullWidth required
              label="Staff ID"
              placeholder="e.g. ADM-001"
              value={data.staffId}
              onChange={set('staffId')}
              error={!!errors.staffId}
              helperText={errors.staffId || ' '}
              sx={brandFieldSx}
              InputProps={adorn(BadgeIcon)}
            />
          </Field>

          <Field span={3}>
            <TextField
              select fullWidth
              label="Gender"
              value={data.gender}
              onChange={set('gender')}
              error={!!errors.gender}
              helperText={errors.gender || 'Optional'}
              sx={brandFieldSx}
            >
              {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </TextField>
          </Field>

          <Field span={6}>
            <TextField
              fullWidth required type="email"
              label="Email Address"
              placeholder="admin@yourhospital.com"
              value={data.email}
              onChange={set('email')}
              error={!!errors.email}
              helperText={errors.email || 'Where system and security notices are sent'}
              sx={brandFieldSx}
              InputProps={adorn(EmailIcon)}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="0244000000"
              value={data.phone}
              onChange={set('phone')}
              error={!!errors.phone}
              helperText={errors.phone || 'Optional - used for account-recovery contact'}
              sx={brandFieldSx}
              InputProps={adorn(PhoneIcon)}
            />
          </Field>
        </FormRow>
      </FormSection>

      {/* -- Credentials -------------------------------------------------- */}
      <FormSection
        title="Sign-in credentials"
        caption="The username is chosen once and kept for the life of the system. The password is temporary - it must be replaced at first sign-in."
      >
        <FormRow>
          <Field span={12}>
            <TextField
              fullWidth required
              label="Username"
              placeholder="e.g. admin.mensah"
              value={data.username}
              onChange={(e) => onChange('username', e.target.value.replace(/\s/g, ''))}
              error={!!errors.username || nameState.status === 'taken'}
              helperText={usernameHelper}
              sx={{
                ...brandFieldSx,
                '& .MuiFormHelperText-root': {
                  color: nameState.status === 'available' && !errors.username ? BRAND.green : undefined,
                  fontWeight: nameState.status === 'available' ? 600 : 400,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="This username is permanent - it cannot be changed after setup" arrow>
                      <LockOutlinedIcon sx={{ fontSize: 19, color: BRAND.navy }} />
                    </Tooltip>
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">{nameAdornment()}</InputAdornment>,
              }}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth required
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={data.password}
              onChange={set('password')}
              error={!!errors.password}
              helperText={errors.password || ' '}
              sx={brandFieldSx}
              InputProps={{
                ...adorn(LockIcon),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth required
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={data.confirmPassword}
              onChange={set('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword || ' '}
              sx={brandFieldSx}
              InputProps={{
                ...adorn(LockIcon),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end">
                      {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Field>

          {/* No strength meter here, on purpose.
              This password is a handover credential with a lifetime of
              minutes — the account is created with first_login = 1, so the
              administrator cannot reach anything until they replace it. A
              meter scoring it "Weak" in red would be nagging about a value
              that is meant to be temporary, and complexity rules would make
              installation fiddly on a site visit. The real policy lands on
              the change-password prompt instead, and this says so. */}
          <Field span={12}>
            <Box
              sx={{
                display: 'flex', gap: 1.5, alignItems: 'flex-start',
                p: 2.25, borderRadius: '12px',
                background: BRAND.greenSoft, border: `1px solid ${BRAND.green}2E`,
              }}
            >
              <LockResetIcon sx={{ fontSize: 20, color: BRAND.greenDark, flexShrink: 0, mt: .125 }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: BRAND.ink, mb: .5 }}>
                  Any password will do here
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.7 }}>
                  Use something simple you can hand over. At their first sign-in the
                  administrator must replace it, and <strong>that</strong> password has to be at
                  least 8 characters with an uppercase letter, a lowercase letter, a
                  number and a symbol.
                </Typography>
              </Box>
            </Box>
          </Field>
        </FormRow>
      </FormSection>
    </Box>
  );
}

export default AdminStep;
